import { forkJoin as observableForkJoin, of as observableOf, Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { InitializationService } from './initialization.service';
import { InitializationStatusCodes } from './initialization.const';
import { InitializationModel } from './initialization.model';
import { LoggerUtil } from '../LoggerService';
import { ConfigService } from '../ConfigService';
import { AuthenticationService } from '../AuthenticationService';
import { AuthStatusService } from '../AuthStatusService';
import { EmployeeSideService } from '../EmployeeSideService';
import { HttpProvider } from '../http/http.provider';

/**
 * InitializationService variant for the employee self-service portal.
 *
 * Replaces the admin bootstrap calls (getAllAccountManager, etc.) with
 * employee-specific ones:
 *   - configService.getConfigByOrg()   � shared config
 *   - employeeSideService.getEmployee() � own profile pre-load
 *
 * Construction is handled by EmployeeInitializationServiceFactory; accessed
 * via ServiceLocator.getEmployeeInitializationService().
 */
export class EmployeeInitializationService extends InitializationService
{
    static logger: LoggerUtil = LoggerUtil.getLoggerUtil('EmployeeInitializationService');

    constructor(
        configService: ConfigService,
        authenticationService: AuthenticationService,
        initializationModel: InitializationModel,
        authStatusService: AuthStatusService,
        private employeeSideService: EmployeeSideService,
        httpProvider: HttpProvider
    )
    {
        // Pass null for accountManagerService � the base class only uses it inside
        // executeBootstrapCalls(), which we fully override here.
        super(configService, authenticationService, initializationModel,
            authStatusService, null as any, httpProvider);
    }

    protected executeBootstrapCalls(): void
    {
        if (this.bootstrapExecuted)
        {
            EmployeeInitializationService.logger.debug(
                'Employee bootstrap already executed � skipping duplicate call');
            return;
        }

        EmployeeInitializationService.logger.debug('Executing employee bootstrap API calls�');
        this.bootstrapExecuted = true;
        this.__state = InitializationStatusCodes.INITIALIZING;

        const configObs = this.configService.getConfigByOrg().pipe(
            map(result => ({ api: 'getConfig', success: true, data: result })),
            catchError(error =>
                observableOf({ api: 'getConfig', success: false, error }))
        );

        const profileObs = this.employeeSideService.getEmployee().pipe(
            map(result => ({ api: 'getEmployee', success: true, data: result })),
            catchError(error =>
                observableOf({ api: 'getEmployee', success: false, error }))
        );

        observableForkJoin([configObs, profileObs]).subscribe(
            (results: Array<any>) =>
            {
                const failures = results.filter(r => !r.success);
                if (failures.length)
                {
                    EmployeeInitializationService.logger.warn(
                        'Some employee bootstrap calls failed (app will continue):', failures);
                }
                EmployeeInitializationService.logger.debug(
                    'Employee bootstrap complete � app is RUNNING');
                this.__state = InitializationStatusCodes.RUNNING;
            },
            (error) =>
            {
                EmployeeInitializationService.logger.error(
                    `Employee bootstrap failed: ${JSON.stringify(error)}`);
                this.bootstrapExecuted = false;
                this.__state = (error && (error.status === 401 || error.status === 403))
                    ? InitializationStatusCodes.UNAUTHENTICATED
                    : InitializationStatusCodes.UNAUTHENTICATED;
            }
        );
    }
}
